import type { Client, Sale } from "@/types";
import { formatCOP, formatNumber } from "@/utils/format";

interface ClientStatsProps {
  clients: Client[];
  sales: Sale[];
}

// Resumen ejecutivo de la base de clientes.
const ClientStats = ({ clients, sales }: ClientStatsProps) => {
  const keys = new Set<string>();
  sales.forEach((s) => {
    if (s.clientId) keys.add(s.clientId);
    if (s.clientName) keys.add(s.clientName.trim().toLowerCase());
  });

  const withSales = clients.filter(
    (c) =>
      (c.id && keys.has(c.id)) ||
      keys.has(c.name.trim().toLowerCase())
  ).length;

  const cities = new Set(clients.map((c) => (c.city ?? "").trim()).filter(Boolean)).size;
  const totalBilled = sales.reduce((acc, s) => acc + s.total, 0);

  const items = [
    {
      icon: "ri-user-3-line",
      label: "Clientes registrados",
      value: formatNumber(clients.length),
      bubble: "bg-primary-500/25 text-primary-100",
    },
    {
      icon: "ri-shopping-bag-3-line",
      label: "Clientes con compras",
      value: formatNumber(withSales),
      bubble: "bg-accent-500/20 text-accent-200",
    },
    {
      icon: "ri-map-pin-2-line",
      label: "Ciudades atendidas",
      value: formatNumber(cities),
      bubble: "bg-secondary-500/25 text-secondary-200",
    },
    {
      icon: "ri-money-dollar-circle-line",
      label: "Facturado a clientes",
      value: formatCOP(totalBilled),
      bubble: "bg-accent-500/15 text-accent-100",
    },
  ];

  return (
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
      {items.map((s) => (
        <div key={s.label} className="glass-panel rounded-lg p-4 animate-fade-up">
          <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${s.bubble}`}>
            <i className={`${s.icon} text-lg`} />
          </span>
          <p className="text-[11px] text-foreground-500 mt-3">{s.label}</p>
          <p className="text-lg md:text-xl font-heading font-extrabold text-foreground-950 mt-0.5 break-words">
            {s.value}
          </p>
        </div>
      ))}
    </div>
  );
};

export default ClientStats;